import { IoTrendingDown, IoTrendingUp } from "react-icons/io5";
import Select from "react-select";
import { defaultDepartment, defaultManagers } from "../helper";
import { useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import conversion from "../../../images/conversion_rate.png";

const CandidateQuality = ({
  pipelineData = [],
  previousPipelineData = [],
  departments,
  hiringManagers,
  userPrivilege
}) => {
  const [candidateQualityState, setCandidateQualityState] = useState({
    department: "",
    hiringManager: "",
  });

  const calculateCounts = (data) => {
      return data
        .filter((job) => {
          if (candidateQualityState.department) {
            return candidateQualityState.department === job.department;
          } else {
            return true;
          }
        })
        .filter((job) => {
          if (candidateQualityState.hiringManager) {
            return (
              candidateQualityState.hiringManager === job.assignhiringmanager
            );
          } else {
            return true;
          }
        })
        .reduce(
          (acc, job) => {
            const totalApps = job.total_apps || 0;
            const rejected = job.rejected || 0;
            const onHold = job.on_hold || 0;
            const interview = job.interview || 0;
            const selected = job.selected || 0;
            const offer = job.offer || 0;
            const hired = job.hired || 0;

            acc.applicationCount += totalApps;
            acc.positionCount += job.positionnumber || 0;
            
            // Subsets calculation
            const actualHired = hired;
            const actualOffer = offer + actualHired;
            const actualInterview = interview + selected + actualOffer;

            acc.hiredCount += actualHired;
            acc.offerCount += actualOffer;
            acc.interviewCount += actualInterview;
            
            return acc;
          },
          {
            applicationCount: 0,
            positionCount: 0,
            hiredCount: 0,
            offerCount: 0,
            interviewCount: 0,
          }
        );
  }

  const jobCounts = useMemo(
    () => calculateCounts(pipelineData),
    [pipelineData, candidateQualityState]
  );
  
  const previousJobCounts = useMemo(
    () => calculateCounts(previousPipelineData),
    [previousPipelineData, candidateQualityState]
  );

  const appPerPos = parseInt(
    (jobCounts.applicationCount || 0) / (jobCounts.positionCount || 1)
  );
  const prevAppPerPos = parseInt(
    (previousJobCounts.applicationCount || 0) /
      (previousJobCounts.positionCount || 1)
  );
  const diffPerPos = appPerPos - prevAppPerPos;

  const hirePerOffer = parseInt(
    ((jobCounts.hiredCount || 0) / (jobCounts.offerCount || 1)) * 100
  );
  const prevHirePerOffer = parseInt(
    ((previousJobCounts.hiredCount || 0) /
      (previousJobCounts.offerCount || 1)) *
      100
  );
  const diffPerOffer = hirePerOffer - prevHirePerOffer;

  const barChart = {
    series: [
      {
        name: "",
        data: [
          jobCounts.interviewCount,
          jobCounts.offerCount,
          jobCounts.hiredCount,
        ],
      },
    ],
    options: {
      chart: {
        type: "bar",
        height: 350,
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          barHeight: "50%",
          dataLabels: {
            position: "bottom",
          },
        },
      },
      colors: ["#4FABFF"],
      applicationCount: jobCounts.applicationCount,
      dataLabels: {
        enabled: true,
        formatter: function (val, { dataPointIndex, w }) {
          const applicationCount = w.config.applicationCount;
          let percentage = ((val || 0) / (applicationCount || 1)) * 100;
          return `${percentage.toFixed(1)}%`;
        },
        style: {
          fontSize: "16px",
          fontWeight: "500",
          colors: ["#000"],
        },
        offsetY: 5,
        dropShadow: {
          enabled: false,
        },
      },
      xaxis: {
        categories: ["Interviews", "Offer", "Hire"],
        labels: {
          rotate: -45,
          trim: true,
        },
      },
      legend: {
        show: false,
      },
      tooltip: {
        y: {
          formatter: function (val, { dataPointIndex, w }) {
            const applicationCount = w.config.applicationCount;
            let percentage = ((val || 0) / (applicationCount || 1)) * 100;
            const category = w.globals.labels[dataPointIndex];

            return `${category} : ${val} <br/> Application to ${category} Rate: ${percentage.toFixed(
              1
            )}%`;
          },
        },
      },
    },
  };

  return (
    <div className=" bg-white h-[37rem]  py-3 items-center  rounded-divRadius shadow-sh">
      <div className="flex items-center justify-between w-full">
        <h2 className="font-semibold  relative  text-[16px] pl-4 leading-[18.75px] before:block before:bg-blue-500 before:w-1 before:content-'' before:absolute before:left-0 before:top-1/2 before:h-2/4 before:mr-4 before:transform before:-translate-y-1/2">
          Candidate Quality
        </h2>
        <div className="flex justify-end space-x-3 pr-4">
          <Select
            placeholder={<div>Select department</div>}
            defaultValue={defaultDepartment}
            options={departments}
            onChange={(option) => {
              setCandidateQualityState((prev) => {
                return {
                  department: option.value,
                  hiringManager: prev.hiringManager,
                };
              });
            }}
            className="text-sm"
          ></Select>
           {userPrivilege !== "Hiring Manager" &&<Select
            placeholder={<div>Select hiring manager</div>}
            defaultValue={defaultManagers}
            onChange={(option) => {
              setCandidateQualityState((prev) => {
                return {
                  department: prev.department,
                  hiringManager: option.value,
                };
              });
            }}
            className="text-sm"
            options={hiringManagers}
          ></Select>}
        </div>
      </div>
      <div className="bg-gray-300 h-[1px] mt-3 w-full"></div>
      <div className="px-6">
        <div className="flex space-x-3 mt-6 justify-between ">
          <div className="rounded-xl w-1/2 flex items-center justify-between border py-3 px-6">
            <div className="flex-1">
              <p className="text-base font-medium">Applications per position</p>
              <p className="text-2xl font-medium">{appPerPos}</p>
            </div>
            {previousPipelineData.length === 0 ? (
              <p className="text-base text-gray-600 font-medium">--</p>
            ) : (
              <div className="flex items-center gap-1.5">
                {diffPerPos > 0 ? (
                  <IoTrendingUp size={30} color="#40B2B7" />
                ) : (
                  <IoTrendingDown size={30} color="#E02734" />
                )}
                <p
                  style={{
                    color: diffPerPos > 0 ? "#40B2B7" : "#E02734",
                  }}
                  className="text-base"
                >
                  {~~Math.ceil((diffPerPos / appPerPos) * 100)}%
                </p>
              </div>
            )}
          </div>
          <div className="rounded-xl w-1/2 flex items-center justify-between border py-3 px-6">
            <div className="flex-1">
              <p className="text-base font-medium">Hire rate per offer</p>
              <p className="text-2xl font-medium">{hirePerOffer}</p>
            </div>
            {previousPipelineData.length === 0 ? (
              <p className="text-base text-gray-600 font-medium">--</p>
            ) : (
              <div className="flex items-center gap-1.5">
                {diffPerOffer > 0 ? (
                  <IoTrendingUp size={30} color="#40B2B7" />
                ) : (
                  <IoTrendingDown size={30} color="#E02734" />
                )}
                <p
                  style={{
                    color: diffPerOffer > 0 ? "#40B2B7" : "#E02734",
                  }}
                  className="text-base"
                >
                  {~~Math.ceil((diffPerOffer / hirePerOffer) * 100)}%
                </p>
              </div>
            )}
          </div>
        </div>
        <div className="rounded-xl border p-4  mt-4">
          <p className="leading-[18.75px] text-[16px] font-semibold">
            Application Conversion Rate
          </p>
          <div className="bg-gray-300 h-[0.09rem] mt-4 w-full"></div>
          {pipelineData.length ? (
            <ReactApexChart
              options={barChart.options}
              series={barChart.series}
              type="bar"
              height={250}
            />
          ) : (
            <div>
              <div className="flex h-[20rem] justify-center w-full items-center">
                <div>
                  <img className="h-28 mx-auto" src={conversion} alt=""></img>
                  <p className="text-center text-sm mt-2 font-semibold">
                    Conversion rate metrics will appear here once you begin
                    filling your positions.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default CandidateQuality;
